import * as React from "react";
import { connect } from "react-redux";
import {Col, Form, Input, Row, Select, Tooltip, InputNumber, Switch} from "antd";
import { Image } from "@models/image";
import ImagePicker from "@components/custom/imagePicker";
import {
  createRequest,
  fetchItemRequest,
  updateRequest
} from "@actions/category";
import { Category, CategoryType, FlagType } from "@models/category";
import TextEditor, { TextEditorRef } from "@components/custom/TextEditor";
import { Checkbox } from "antd";
import { SelectionMode } from "@models/index";
import { FormattedMessage } from "react-intl";
import CategorySelection from "@components/categories/Selection";
import { CategoryTypeOptions } from "@models/category";
import categoryApi from "@api/category";
import withFormWrapper from "../../hocs/withFormWrapper";

const { Option } = Select;

interface PropsFromState {
  loading: boolean;
  errors?: string;
  form: any;
  category?: Category;
}

interface PropsFromDispatch {
  createRequest: typeof createRequest;
  updateRequest: typeof updateRequest;
  fetchItemRequest: typeof fetchItemRequest;
}

type CategoryFormAllProps = PropsFromState & PropsFromDispatch;

type CategoryFormStateShape = {
  visible: boolean;
  title: string;
  slug: string;
  body: string;
  parent_category: Category | null;
  cover_image_file: Image | null;
  thumbnail_image_file: Image | null;
  type: CategoryType;
  flag: string;
  iran_access: boolean;
  compact_header: boolean;
};

const FlagOptions = [
  {
    label: (
      <FormattedMessage
        id="app.show_in_filter"
        defaultMessage={`Show in filter`}
      />
    ),
    value: FlagType.CLIENT_FILTER
  },
  // { label: 'Has Page', value: FlagTag.HAS_PAGE },
];

class CategoryForm extends React.Component<
  CategoryFormAllProps,
  CategoryFormStateShape
> {
  constructor(props) {
    super(props);
    const { category } = props;
    this.state = {
      visible: false,
      ...this.getStateFromProps(category)
    };
  }

  private textEditorRef = React.createRef<TextEditorRef>();

  getStateFromProps = (category: Category | null) => {
    const obj = category || {};
    return {
      title: obj.title || "",
      slug: obj.slug || "",
      body: obj.body || "",
      cover_image_file: obj.cover_image_file || null,
      thumbnail_image_file: obj.thumbnail_image_file || null,
      type: obj.type || CategoryType.NONE,
      parent_category: obj.parent_category || null,
      order: obj.order,
      id: obj.id,
      flag: obj.flag || FlagType.NONE,
      iran_access: obj.iran_access,
      compact_header: obj.compact_header || false
    };
  };

  fetchItem = () => {
    categoryApi
      .getItem({
        params: {
          id: this.state.id
        }
      })
      .then(({ data }) => {
        const obj = this.getStateFromProps(data);
        this.setState(obj);
      })
      .catch(error => console.log(error));
  };

  getValues = () => {
    let returnValues = null;
    this.props.form.setFieldsValue({
      thumbnail_image_file: this.state.thumbnail_image_file,
      cover_image_file: this.state.cover_image_file
    });
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        returnValues = {
          ...values,
          cover_image_file_id: this.state.cover_image_file ? this.state.cover_image_file.id : null,
          thumbnail_image_file_id: this.state.thumbnail_image_file ? this.state.thumbnail_image_file.id : null,
          body: this.textEditorRef.current ? this.textEditorRef.current.value : "",
          parent_category_id: this.state.parent_category ? this.state.parent_category.id : null,
          flag: this.state.flag || FlagType.NONE,
        };
      }
    });

    return {
      payload: returnValues,
      id: this.props.category ? this.props.category.id : null
    };
  };

  onChangeFlags = checkedValues => {
    this.setState({
      flag: checkedValues.join("|")
    });
  };

  setCoverImage = (image: Image) => {
    this.setState({
      cover_image_file: image
    });
  };

  setThumbnailImage = (image: Image) => {
    this.setState({
      thumbnail_image_file: image
    });
  };

  onChangeParent = (category: Category): void => {
    this.setState({
      parent_category: category
    });
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    return (
      <Form className={'category-form'}>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item className={"no-label video-poster-wrapper"}>
              {getFieldDecorator("thumbnail_image_file", {
                rules: [{
                    required: true,
                    message: (
                      <FormattedMessage
                        id="app.thumbnail_image"
                        defaultMessage={`Thumbnail Image`}
                      />
                    )
                  }]
              })(
                <ImagePicker
                  onSelectImage={this.setThumbnailImage}
                  selectedImage={this.state.thumbnail_image_file}
                >
                  <div
                    className={`image-wrapper`}
                    style={{
                      backgroundImage: `url(${
                        this.state.thumbnail_image_file
                          ? this.state.thumbnail_image_file.path
                          : ""
                      })`
                    }}
                  >
                    <FormattedMessage
                      id="app.thumbnail_image"
                      defaultMessage={`Thumbnail Image`}
                    />
                  </div>
                </ImagePicker>
              )}
            </Form.Item>
          </Col>
          <Col span={16} className={"cover-wrapper category-cover-wrapper"}>
            <Form.Item className={"no-label video-cover-wrapper"}>
              {getFieldDecorator("cover_image_file", {
                rules: [{
                    required: true,
                    message: (
                      <FormattedMessage
                        id="app.cover_image_is_required"
                        defaultMessage={`Cover image is required!`}
                      />
                    )
                  }]
              })(
                <ImagePicker
                  onSelectImage={this.setCoverImage}
                  selectedImage={this.state.cover_image_file}
                >
                  <div
                    className={`cover-image`}
                    style={{
                      backgroundImage: `url(${
                        this.state.cover_image_file
                          ? this.state.cover_image_file.path
                          : ""
                      })`
                    }}
                  >
                    <FormattedMessage
                      id="app.cover_image"
                      defaultMessage={`Cover Image`}
                    />
                  </div>
                </ImagePicker>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={8}>
            <Form.Item
              label={<FormattedMessage id="app.type" defaultMessage={`Type`} />}
            >
              {getFieldDecorator("type", {
                initialValue: this.state.type
              })(
                <Select>
                  {CategoryTypeOptions.map((opt, index) => (
                    <Option key={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={
                <FormattedMessage
                  id="app.parent_category"
                  defaultMessage={`Parent Category`}
                />
              }>
              {getFieldDecorator("parent_category_id", {
                initialValue: this.state.parent_category ? this.state.parent_category : null
              })}
              <CategorySelection
                mode={SelectionMode.DEFAULT}
                initialValue={this.state.parent_category}
                onChange={this.onChangeParent}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={<FormattedMessage id="app.title" defaultMessage={`Title`} />}>
              {getFieldDecorator("title", {
                initialValue: this.state.title,
                rules: [{
                    required: true,
                    whitespace: true,
                    message: (
                      <FormattedMessage
                        id="app.title_is_required"
                        defaultMessage={`Title is required`}
                      />
                    )
                  }]
              })(<Input />)}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={<FormattedMessage id="app.slug" defaultMessage={`Slug`} />}>
              {getFieldDecorator("slug", {
                initialValue: this.state.slug,
                rules: [
                  {
                    whitespace: true,
                    required: true,
                    message: (
                      <FormattedMessage
                        id="app.slug_is_required"
                        defaultMessage={`Slug is required`}
                      />
                    )
                  }
                ]
              })(<Input />)}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={
                <FormattedMessage
                  id="app.display_order"
                  defaultMessage={`Display Order`}
                />
              }
            >
              {getFieldDecorator("order", {
                initialValue: this.state.order
              })(<InputNumber min={0} />)}
            </Form.Item>
          </Col>

          <Col span={8} style={{textAlign:"center"}}>
            <Col span={24}>
              <Checkbox.Group
                options={FlagOptions}
                defaultValue={this.state.flag.split("|")}
                onChange={this.onChangeFlags}
              />
            </Col>
            <Col span={24}>
              <Form.Item style={{paddingRight: "30%"}} label={<FormattedMessage id="iran_access" defaultMessage={`Iran Access`}/>}>
                {getFieldDecorator("iran_access", {
                  initialValue: this.state.iran_access
                })(
                  <Switch defaultChecked={this.state.iran_access}/>
                )}
              </Form.Item>
            </Col>
          </Col>
            <Col span={4}>
              <Form.Item style={{paddingRight: "30%"}} label={<FormattedMessage id="compact_header" defaultMessage={`Compact Header`}/>}>
                {getFieldDecorator("compact_header", {
                  initialValue: this.state.compact_header,
                  valuePropName: "checked"
                })(
                  <Switch defaultChecked={this.state.compact_header}/>
                )}
              </Form.Item>
            </Col>
          <Col span={24}>
            <Form.Item
              label={<FormattedMessage id="app.body" defaultMessage={`Body`} />}
            >
              {getFieldDecorator("body")(
                <TextEditor ref={this.textEditorRef} text={this.state.body} />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    );
  }
}



export default withFormWrapper(
  Form.create({ name: "category-form" })(CategoryForm)
);
